<template>
  <transition name="gmodal-fade">
    <div class="gmodal-overlay" @click.self="close">
      <div class="gmodal">

        <!-- Header -->
        <div class="gmodal__header">
          <div class="gmodal__header-icon">
            <i class="fa fa-layer-group"></i>
          </div>
          <div class="gmodal__header-text">
            <div class="gmodal__title">{{ is_edit ? 'Update Group' : 'New Group' }}</div>
            <div class="gmodal__sub">{{ is_edit ? 'Edit group details below' : 'Fill in the details to create a group' }}</div>
          </div>
          <button class="gmodal__close" @click="close">
            <i class="fa fa-times"></i>
          </button>
        </div>

        <!-- Body -->
        <div class="gmodal__body">
          <div class="form-grid">

            <!-- Group Name -->
            <div class="form-field">
              <label class="field-label">Group Name <span class="req">*</span></label>
              <input
                type="text"
                class="field-input"
                :class="{ 'field-input--error': errors['name'] }"
                v-model="group.name"
                placeholder="Enter group name"
              />
              <span class="field-error" v-if="errors['name']">{{ errors['name'][0] }}</span>
            </div>

            <!-- Description -->
            <div class="form-field">
              <label class="field-label">Description <span class="req">*</span></label>
              <input
                type="text"
                class="field-input"
                :class="{ 'field-input--error': errors['description'] }"
                v-model="group.description"
                placeholder="Enter description"
              />
              <span class="field-error" v-if="errors['description']">{{ errors['description'][0] }}</span>
            </div>

          </div>

          <!-- Image & Featured row -->
          <div class="media-row">

            <!-- Image preview + upload -->
            <div class="image-section">
              <label class="field-label">Group Image</label>
              <div class="image-preview">
                <canvas
                  id="imageCanvas"
                  ref="imageCanvas"
                  v-if="showImage"
                  class="canvas-preview"
                ></canvas>
                <div
                  v-else-if="group.image1"
                  class="image-placeholder"
                  :style="'background-image: url(' + group.image1 + ')'"
                ></div>
                <div v-else class="image-placeholder image-placeholder--empty">
                  <i class="fa fa-image"></i>
                </div>
              </div>
              <label class="upload-btn">
                <input type="file" id="imageLoader" @change="updateCanvasImage" hidden accept="image/*" />
                <i class="fa fa-upload"></i> Upload Image
              </label>
              <span class="field-error" v-if="errors['image']">{{ errors['image'][0] }}</span>
            </div>

            <!-- Featured toggle -->
            <div class="featured-section">
              <label class="field-label">Featured</label>
              <label class="toggle-switch">
                <input type="checkbox" v-model="group.is_featured" />
                <span class="toggle-track">
                  <span class="toggle-thumb"></span>
                </span>
              </label>
              <span class="field-error" v-if="errors['is_featured']">{{ errors['is_featured'][0] }}</span>
            </div>

          </div>
        </div>

        <!-- Footer -->
        <div class="gmodal__footer">
          <button class="gmodal__btn gmodal__btn--cancel" @click="close">Cancel</button>
          <button class="gmodal__btn gmodal__btn--save" @click="save">
            <i class="fa fa-check"></i>
            {{ is_edit ? 'Update' : 'Save' }}
          </button>
        </div>

      </div>
    </div>
  </transition>
</template>

<script>
import { mapGetters } from "vuex";
import { CREATE_GROUP } from "@/core/services/store/actions.type";

export default {
  props: {
    showModal: { type: Boolean, default: false },
    is_edit:   { type: Boolean, default: false },
    group: {
      type: Object,
      default: () => ({ name: "", description: "", image: "", is_featured: false, id: 0 })
    }
  },
  data() {
    return { showImage: false, errors: [] };
  },
  computed: {
    ...mapGetters([])
  },
  methods: {
    close() {
      this.$emit("closemodal");
    },
    save() {
      this.$store
        .dispatch(CREATE_GROUP, this.group)
        .then(() => {
          this.$toastr.s(this.is_edit ? "Group successfully updated" : "Group successfully created");
          this.$emit("closemodal");
        })
        .catch(err => { this.errors = err; });
    },
    updateCanvasImage(e) {
      this.showImage = true;
      const files = e.target.files;
      const reader = new FileReader();
      reader.onload = ev => {
        const img = new Image();
        img.onload = () => { this.drawCanvasImage(img); };
        img.src = ev.target.result;
      };
      reader.readAsDataURL(files[0]);
    },
    drawCanvasImage(imageObj) {
      const canvas = this.$refs.imageCanvas;
      canvas.width  = 120;
      canvas.height = (100 * imageObj.height) / imageObj.width;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(imageObj, 0, 0, 120, (100 * imageObj.height) / imageObj.width);
      this.group.image = canvas.toDataURL("image/png");
    }
  }
};
</script>

<style lang="scss" scoped>
$font:   'Segoe UI', sans-serif;
$blue:   #389ad4;
$dark:   #0d1b3e;
$border: #dde2ec;

/* ── overlay ── */
.gmodal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(13,27,62,0.5);
  backdrop-filter: blur(5px);
  z-index: 400;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

/* ── modal box ── */
.gmodal {
  background: #fff;
  border-radius: 18px;
  width: 100%;
  max-width: 560px;
  box-shadow: 0 24px 64px rgba(0,0,0,0.18);
  font-family: $font;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  /* ── header ── */
  &__header {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 22px 24px;
    background: linear-gradient(135deg, #f8faff, #f0f5ff);
    border-bottom: 1px solid $border;
  }

  &__header-icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: linear-gradient(135deg, $blue, #2277aa);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 17px;
    flex-shrink: 0;
  }

  &__header-text { flex: 1; }
  &__title       { font-size: 15px; font-weight: 700; color: $dark; }
  &__sub         { font-size: 12px; color: #7a8499; margin-top: 2px; }

  &__close {
    width: 32px;
    height: 32px;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: #a0aab8;
    font-size: 14px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s, color 0.15s;
    &:hover { background: #e8eef8; color: $dark; }
  }

  /* ── body ── */
  &__body {
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  /* ── footer ── */
  &__footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    padding: 16px 24px;
    border-top: 1px solid $border;
    background: #fafbfd;
  }

  &__btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 38px;
    padding: 0 20px;
    border-radius: 9px;
    font-size: 13px;
    font-weight: 600;
    font-family: $font;
    cursor: pointer;
    border: none;
    transition: background 0.15s, box-shadow 0.15s;

    &--cancel {
      background: #f0f3fa;
      color: $dark;
      border: 1.5px solid $border;
      &:hover { background: #e4eaf5; }
    }

    &--save {
      background: $blue;
      color: #fff;
      &:hover { background: #2d85bb; box-shadow: 0 4px 12px rgba(56,154,212,0.3); }
    }
  }
}

/* ── form grid ── */
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 480px) { grid-template-columns: 1fr; }
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.field-label {
  font-size: 12px;
  font-weight: 600;
  color: #5a6278;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.req { color: #b42318; }

.field-input {
  height: 38px;
  border: 1.5px solid $border;
  border-radius: 9px;
  padding: 0 12px;
  font-size: 13px;
  font-family: $font;
  color: $dark;
  background: #fff;
  transition: border-color 0.15s, box-shadow 0.15s;
  outline: none;

  &:focus { border-color: $blue; box-shadow: 0 0 0 3px rgba(56,154,212,0.12); }
  &--error { border-color: #b42318; }
  &::placeholder { color: #b0b8cc; }
}

.field-error {
  font-size: 11px;
  color: #b42318;
}

/* ── media row ── */
.media-row {
  display: flex;
  gap: 24px;
  align-items: flex-start;
}

/* ── image section ── */
.image-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.image-preview {
  width: 130px;
  height: 88px;
  border-radius: 10px;
  border: 1.5px solid $border;
  overflow: hidden;
  background: #f4f6fb;
}

.canvas-preview { width: 130px; display: block; }

.image-placeholder {
  width: 100%;
  height: 100%;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;

  &--empty {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 28px;
    color: #c5d0e0;
  }
}

.upload-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: $blue;
  color: #fff;
  border-radius: 8px;
  padding: 7px 14px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: #2d85bb; }
}

/* ── featured section ── */
.featured-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* ── CSS toggle switch ── */
.toggle-switch {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  user-select: none;

  input { display: none; }

  .toggle-track {
    width: 40px;
    height: 22px;
    border-radius: 11px;
    background: #dde2ec;
    position: relative;
    transition: background 0.2s;
    display: flex;
    align-items: center;
    padding: 0 3px;
  }

  .toggle-thumb {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 4px rgba(0,0,0,0.2);
    transition: transform 0.2s;
    position: relative;
    z-index: 1;
  }

  input:checked ~ .toggle-track {
    background: #389ad4;
    .toggle-thumb { transform: translateX(18px); }
  }
}

/* ── transitions ── */
.gmodal-fade-enter-active, .gmodal-fade-leave-active { transition: opacity 0.22s; }
.gmodal-fade-enter-active .gmodal, .gmodal-fade-leave-active .gmodal { transition: transform 0.22s, opacity 0.22s; }
.gmodal-fade-enter .gmodal, .gmodal-fade-leave-to .gmodal { transform: scale(0.94) translateY(10px); opacity: 0; }
.gmodal-fade-enter, .gmodal-fade-leave-to { opacity: 0; }
</style>
